/**
 * `DispatchModal` — confirms `placed → out_for_delivery` for a COD order and
 * POSTs to `/v1/admin/orders/:id/dispatch` with `{ driver_staff_id }`.
 *
 * The driver is chosen from a `<Select>` populated by
 * `GET /v1/admin/staff?role=wmart_delivery` (the staff-list endpoint shipped
 * with the Team feature). This replaced the original P17-T11 UUID-paste
 * fallback, which existed only because no staff-list endpoint was available
 * at the time.
 *
 * Rendered inside `<Modal>` — ESC + click-outside + focus-trap + scroll-lock
 * all come from the primitive.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Button, Modal, Select } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type {
  AdminOrderDetailDTO,
  StaffDTO,
  StaffListResponse,
} from "@wmart/shared-types";

interface DispatchFormValues {
  driver_staff_id: string;
}

interface Props {
  order: AdminOrderDetailDTO;
  onSuccess: () => void | Promise<void>;
  onClose: () => void;
}

function driverLabel(d: StaffDTO): string {
  const name = d.name ?? d.email ?? d.id;
  return d.department ? `${name} · ${d.department}` : name;
}

export function DispatchModal({ order, onSuccess, onClose }: Props) {
  const { notify, notifyProblem } = useToast();
  const [drivers, setDrivers] = useState<StaffDTO[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<DispatchFormValues>({
    defaultValues: { driver_staff_id: "" },
  });

  // Load the delivery-driver roster on open.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.get<StaffListResponse>(
          "/v1/admin/staff?role=wmart_delivery&limit=200",
        );
        if (cancelled) return;
        // Exclude deactivated staff — they must not be assignable.
        const active = data.items.filter((d) => !d.deleted_at);
        setDrivers(active);
        // Preselect when there's exactly one driver.
        if (active.length === 1) {
          reset({ driver_staff_id: active[0]!.id });
        }
      } catch (err) {
        if (cancelled) return;
        setLoadError(true);
        if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
        else notify("error", "Failed to load delivery drivers");
      } finally {
        if (!cancelled) setLoadingDrivers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmit(values: DispatchFormValues) {
    try {
      // 204 No Content from /dispatch — we don't read the response body.
      await api.post(`/v1/admin/orders/${order.id}/dispatch`, {
        driver_staff_id: values.driver_staff_id,
      });
      notify("success", "Order dispatched for delivery");
      await onSuccess();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Dispatch failed");
    }
  }

  const noDrivers = !loadingDrivers && !loadError && drivers.length === 0;
  const canDispatch = !isSubmitting && !loadingDrivers && drivers.length > 0;

  return (
    <Modal
      open
      onClose={onClose}
      title="Dispatch for delivery"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Back
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={!canDispatch}
            loading={isSubmitting}
          >
            Dispatch
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <p className="text-body-sm text-ink-700">
          Confirm dispatching order{" "}
          <span className="font-mono">{order.id.slice(0, 8)}…</span> for COD
          delivery. The order will move to{" "}
          <strong>out for delivery</strong> and the customer will be notified.
        </p>

        {loadingDrivers ? (
          <p className="text-body-sm text-ink-500">Loading drivers…</p>
        ) : noDrivers ? (
          <p className="text-body-sm text-ink-700">
            No delivery drivers yet. Add a staff member with the Delivery role
            in{" "}
            <Link
              to="/team/new"
              className="rounded-sm text-accent underline underline-offset-2 outline-none focus-visible:shadow-focus"
            >
              Team
            </Link>
            .
          </p>
        ) : (
          <Select
            label="Assign driver"
            helper="Drivers are staff members with the Delivery role."
            error={errors.driver_staff_id?.message}
            {...register("driver_staff_id", { required: "Select a driver" })}
          >
            <option value="">Select a driver…</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>
                {driverLabel(d)}
              </option>
            ))}
          </Select>
        )}

        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
